import { NormalPeoplePicker, DefaultButton, Dialog, DialogFooter, DialogType, Label, PrimaryButton, TextField, IPersonaProps, ComboBox, getInitials, getPersonaInitialsColor, SelectableOptionMenuItemType } from "@fluentui/react";
import { useId } from "@fluentui/react-hooks";
import { useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { PodCreateRequest } from "../../../api/pods.types";
import { RootState } from "../../../redux/types";
import PodErrorDrawer from "../PodErrorDrawer";
import PodPlaceholder from "../PodPlaceholder";
import PodStatusSpinner, { PodStatusSpinnerState } from "../PodStatusSpinner";
import { PodStateContext } from "../state/reducer";
import { getMarketRegions, getProviderList } from "../state/selectors";
import styles from './CreatePodDialog.module.scss';

interface Props {
  show: boolean,
  loading?: boolean,
  error: string,
  onSubmit: (data: PodCreateRequest, navigateOnSuccess: boolean) => void;
  onDismiss: () => void;
}

const CreatePodDialog = ({show, loading, error, onSubmit, onDismiss: dismissCallback}: Props) => {
  const dialogLabelId: string = useId('dialogLabel');
  const physicianLabelId: string = useId('physicianLabel');
  const optionKeyDepressed = useSelector((state: RootState) => state.ui.optionKeyDepressed);

  const { useContextSelector } = useContext(PodStateContext);
  const marketRegionData = useContextSelector(getMarketRegions);
  const providerList = useContextSelector(getProviderList);

  const dropdownsAreEmpty = marketRegionData.marketGroups.length === 0 || marketRegionData.regions.length === 0;

  const [name, setName] = useState('');
  const [region, setRegion] = useState<string | null>(null);
  const [marketGroups, setMarketGroups] = useState<string[]>([]);
  const [selectedPhysician, setSelectedPhysician] = useState<IPersonaProps>();
  const [description, setDescription] = useState('');

  const [suppressError, setSuppressError] = useState(false);

  useEffect(() => {
    setSuppressError(false);
  }, [error]);

  const resetForm = () => {
    setName('');
    setRegion(null);
    setMarketGroups([]);
    setSelectedPhysician(undefined);
    setDescription('');
    setSuppressError(true);
  }

  useEffect(() => {
    let delay: NodeJS.Timeout | null = null;

    if (!show) {
      // Give the form time to fade out before resetting the fields.
      delay = setTimeout(() => resetForm(), 500);
    } else {
      resetForm();
    }

    return () => {
      if (delay) clearTimeout(delay);
    }
  }, [show]);

  const regionOptions = useMemo(() => {
    return marketRegionData.regions.map(region => ({ key: region, text: region }));
  }, [marketRegionData]);

  const marketGroupOptions = useMemo(() => {
    return marketRegionData.marketGroups.filter(({region: thisRegion}) => thisRegion === region);
  }, [marketRegionData, region]);

  useEffect(() => {
    setMarketGroups([]);
    setSelectedPhysician(undefined);
  }, [region]);

  useEffect(() => {
    setSelectedPhysician(undefined);
  }, [marketGroups]);

  useEffect(() => {
    setSuppressError(true);
  }, [selectedPhysician]);

  /**
   * Physicians are never assigned to market groups; they're assigned to markets.
   * Because of this, whenever the list of selected groups changes, we expand it
   * into a list of all of the markets within those groups, then filter the list
   * of providers based on whether they share any markets with this generated
   * set.
   */
  const marketPhysicians = useMemo(() => {
    const markets = marketRegionData.marketGroups.filter(group => marketGroups.includes(group.key)).flatMap(group => group.markets);

    if (markets.length === 0) {
      return [];
    } else {
      return providerList.physicians.filter(({ markets: physicianMarkets }) => markets.some(market => physicianMarkets.indexOf(market) > -1));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketGroups, providerList]);

  const submitCallback = (navigateOnSuccess = false) => {
    const physicianId = selectedPhysician?.key as string | undefined;

    if (!region || !physicianId) return;
    // Collect data from form state
    // Send to parent
    const requestData: PodCreateRequest = {
      name: name,
      description: description,
      region: region,
      markets: marketGroups.filter(groupKey => groupKey !== 'SelectAll'),
      leadProviderId: physicianId,
    };

    onSubmit(requestData, navigateOnSuccess);
  }

  if (dropdownsAreEmpty) {
    return (
      <Dialog
        hidden={!show}
        onDismiss={dismissCallback}
        dialogContentProps={{
          type: DialogType.largeHeader,
          title: 'Create pod',
          closeButtonAriaLabel: 'Close',
        }}
        modalProps={{
          titleAriaId: dialogLabelId,
          isBlocking: true,
        }}
      >
        <div className={styles.emptyState}>
          <PodPlaceholder full iconName="Globe2" text="Unable to load markets" />
        </div>
        <DialogFooter>
          <PrimaryButton onClick={dismissCallback}>OK</PrimaryButton>
        </DialogFooter>
      </Dialog>
    );
  }

  const createButtonText = !optionKeyDepressed
    ? 'Create'
    : 'Create and view';

  return (
    <Dialog
      hidden={!show}
      onDismiss={dismissCallback}
      dialogContentProps={{
        type: DialogType.largeHeader,
        title: 'Create pod',
        closeButtonAriaLabel: 'Close',
      }}
      modalProps={{
        titleAriaId: dialogLabelId,
        isBlocking: true,
      }}
    >
      <div className={styles.errorWrapper}>
        <div className={styles.fieldGrid}>
          <TextField
            label="Name"
            onChange={(_, value) => { setName(value ?? '') }}
            className={styles.nameField}
          />
          <ComboBox
            label="Region"
            options={regionOptions}
            selectedKey={region}
            allowFreeform={true}
            onChange={(_, option) => { setRegion(option?.text ?? '') }}
            className={styles.regionField}
          />
          <ComboBox
            label="Market groups"
            options={region
              ? [
                {key: 'SelectAll', text: 'Select All', itemType: SelectableOptionMenuItemType.SelectAll},
                {key: 'divider', text: '-', itemType: SelectableOptionMenuItemType.Divider},
                ...marketGroupOptions
              ]
              : []
            }
            className={styles.marketField}
            disabled={!region}
            allowFreeform={false}
            multiSelect
            selectedKey={marketGroups}
            onChange={(_, option) => {
              if (!option || option.key === 'divider') return;

              if (option.key === 'SelectAll') {
                if (option.selected) {
                  setMarketGroups([...marketGroupOptions.map(option => option.key), 'SelectAll']);
                } else {
                  setMarketGroups([]);
                }
                return;
              }

              const changedKey = option.key as string;
              if (option.selected) {
                const newMarketGroups = marketGroups.filter(groupKey => groupKey !== 'SelectAll');

                if (newMarketGroups.indexOf(changedKey) === -1) {
                  newMarketGroups.push(changedKey);
                }

                if (newMarketGroups.length === marketGroupOptions.length) {
                  newMarketGroups.push('SelectAll');
                }

                setMarketGroups(newMarketGroups);
              } else {
                setMarketGroups(marketGroups.filter(marketGroupKey => marketGroupKey !== changedKey && marketGroupKey !== 'SelectAll'));
              }
            }}
          />
          <div className={styles.physicianField}>
            <Label
              htmlFor={physicianLabelId}
              disabled={!region || marketGroups.length === 0}
            >
              Physician
            </Label>
            <NormalPeoplePicker
              inputProps={{
                id: physicianLabelId,
              }}
              itemLimit={1}
              disabled={!region || marketGroups.length === 0}
              onResolveSuggestions={(filterText, _) => {
                const filteredPhysicians: IPersonaProps[] = marketPhysicians.filter(physician => {
                  const filterLower = filterText.toLocaleLowerCase();
                  return physician.fullName.toLowerCase().indexOf(filterLower) > -1 || physician.npi.indexOf(filterLower) > -1;
                }).map(physician => {
                  const name = physician.fullName.split(",")?.[0];
                  return {
                    key: physician.id,
                    text: physician.fullName,
                    imageUrl: physician.avatarUrl || undefined,
                    imageInitials: getInitials(name, false),
                    initialsColor: getPersonaInitialsColor({ text: name })
                  }
                });
                return filteredPhysicians;
              }}
              selectedItems={selectedPhysician ? [selectedPhysician] : []}
              onChange={(items) => {
                setSelectedPhysician(items?.[0]);
              }}
              pickerSuggestionsProps={{
                suggestionsHeaderText: '',
                noResultsFoundText: 'No providers found',
                loadingText: 'Loading',
                showRemoveButtons: false,
                suggestionsAvailableAlertText: 'Provider suggestions available',
                suggestionsContainerAriaLabel: 'Suggested providers',
              }}
              className={styles.peoplePicker}
            />
          </div>
          <TextField
            label="Description"
            multiline
            rows={3}
            autoAdjustHeight
            className={styles.descriptionField}
            value={description}
            onChange={(_, value) => setDescription(value ?? '')}
          />
        </div>
        <PodErrorDrawer show={!!error && !suppressError} text={error} />
      </div>
      <div className={styles.footerWrapper}>
        <div className={styles.footerLeft}>
        </div>
        <DialogFooter styles={{ actions: { lineHeight: '1em', width: 'auto' } }}>
          <div className={styles.statusContainer}>
            { loading && (
              <PodStatusSpinner state={PodStatusSpinnerState.loading} />
            )}
            <PrimaryButton
              disabled={!!loading || !name || !region || marketGroups.length === 0 || !selectedPhysician || !description}
              onClick={() => {
                /**
                 * If the user option-clicks the button, create the pod and then navigate to it.
                 * The optionKeyDepressed boolean has a tendency to be a bit sticky, especially
                 * when changing focus between elements (e.g., holding option and focusing an
                 * input, then letting go). However, it needs to be used here since the button
                 * text uses it for its global state, and using e.altKey could result in a
                 * discontinuity where the button text says "Create and view," but clicking only
                 * creates. Having both things potentially be sticky is better than having only
                 * one of them be this way and misleading the user.
                 */
                if (optionKeyDepressed) submitCallback(true);
                else submitCallback(false);
              }}
            >
              {loading ? 'Creating...' : createButtonText}
            </PrimaryButton>
            <DefaultButton disabled={!!loading} onClick={dismissCallback}>Cancel</DefaultButton>
          </div>
        </DialogFooter>
      </div>
    </Dialog>
  )
};

export default CreatePodDialog;